import { useEffect, useRef, useState } from "react";
import { _ } from "i18n";
import { X } from "lucide-react";
import type { TeamPlayer } from "../../../types";

interface PlayerPickerPopoverProps {
	players: TeamPlayer[];
	taggedPlayerIds: Set<number>;
	onSelect: (player: TeamPlayer) => void;
	onRemove: (player: TeamPlayer) => void;
	onClose: () => void;
}

export function PlayerPickerPopover({ players, taggedPlayerIds, onSelect, onRemove, onClose }: PlayerPickerPopoverProps) {
	const [search, setSearch] = useState("");
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleMouseDown = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				onClose();
			}
		};
		document.addEventListener("mousedown", handleMouseDown);
		return () => document.removeEventListener("mousedown", handleMouseDown);
	}, [onClose]);

	const filtered = players.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

	return (
		<div
			ref={containerRef}
			className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-64 flex flex-col"
		>
			<div className="p-2 border-b border-gray-100">
				<input
					autoFocus
					type="text"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder={_("discover.gameComment.form.comment.label")}
					className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md outline-none focus:border-blue-400"
				/>
			</div>
			<div className="overflow-y-auto max-h-48">
				{filtered.length === 0 ? (
					<div className="px-3 py-4 text-sm text-gray-400 text-center">{_("message.searching_player.none")}</div>
				) : (
					filtered.map((player) => {
						const isTagged = taggedPlayerIds.has(player.id);
						return (
							<button
								key={player.id}
								type="button"
								onClick={() => (isTagged ? onRemove(player) : onSelect(player))}
								className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${isTagged ? "bg-blue-50" : ""}`}
							>
								{player.photo ? (
									<img src={player.photo} alt={player.name} className="w-6 h-6 rounded-full object-cover shrink-0" />
								) : (
									<div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold shrink-0">
										{player.name.charAt(0).toUpperCase()}
									</div>
								)}
								<span className={`flex-1 truncate ${isTagged ? "text-blue-700 font-medium" : "text-gray-800"}`}>
									{player.name}
								</span>
								{isTagged && <X className="w-3 h-3 text-blue-400 shrink-0" />}
							</button>
						);
					})
				)}
			</div>
		</div>
	);
}
